import { StreamActions } from "@hotwired/turbo"
import * as bootstrap from "bootstrap"

// Custom Turbo Stream actions — the Turbo-native replacements for the
// CableReady operations still needed by stream responses that don't trigger
// a page render (renders close modals via BaseModal's turbo:before-render
// hook, so most flows don't need these).

// <turbo-stream action="modal_close"> — close the open Bootstrap modal.
// With stacked modals (a modal opened from inside another), close the
// innermost one; document order puts nested modals last.
StreamActions.modal_close = function () {
  const modals = document.querySelectorAll(".modal.show")
  if (!modals.length) { return }

  bootstrap.Modal.getInstance(modals[modals.length - 1])?.hide()
}

// <turbo-stream action="modal_open" target="pdf-download-modal"> — open a
// Bootstrap modal by element id (port of the CableReady openModal op).
StreamActions.modal_open = function () {
  this.targetElements.forEach(el => bootstrap.Modal.getOrCreateInstance(el).show())
}

// <turbo-stream action="poll" url="..." delay="1000"> — fetch a turbo-stream
// endpoint after a delay and render its response. Port of the CableReady
// crFetch polling op (PDF export progress).
StreamActions.poll = function () {
  const url = this.getAttribute("url")
  const delay = Number(this.getAttribute("delay") || 0)

  setTimeout(() => {
    fetch(url, { headers: { "Accept": "text/vnd.turbo-stream.html" } })
      .then(r => r.text())
      .then(html => window.Turbo.renderStreamMessage(html))
  }, delay)
}

// <turbo-stream action="progress_bar" targets="..." value="42"> — set a
// Bootstrap progress bar's width/label (port of updateProgressBar).
StreamActions.progress_bar = function () {
  const value = this.getAttribute("value")

  this.targetElements.forEach(bar => {
    bar.style.width = `${value}%`
    bar.setAttribute("aria-valuenow", value)
    bar.innerText = `${value}%`
  })
}

// <turbo-stream action="download_file" url="..." filename="..."> — trigger a
// file download (same-origin) or open in a new tab (port of downloadFile).
StreamActions.download_file = function () {
  const url = this.getAttribute("url")
  const filename = this.getAttribute("filename")

  const link = document.createElement("a")
  link.href = url

  if (url.startsWith("/") || url.startsWith(window.location.origin)) {
    link.download = filename || url.substr(url.lastIndexOf("/") + 1)
  } else {
    link.target = "_blank"
  }
  link.click()
}

// <turbo-stream action="visit" url="https://..."> — client-side navigation,
// for redirect targets a Turbo form submission cannot follow itself (e.g. a
// cross-origin Stripe checkout URL). Turbo.visit falls back to a full
// location change for external URLs.
StreamActions.visit = function () {
  window.Turbo.visit(this.getAttribute("url"))
}

// <turbo-stream action="add_slim_select_option" targets="#metric_ids"
//               text="..." value="..." selected="true">
// — add (and optionally select) an option on a SlimSelect-enhanced <select>.
// Port of the CableReady addSlimSelectOption operation (quick-add metric).
StreamActions.add_slim_select_option = function () {
  const option = {
    text: this.getAttribute("text"),
    value: this.getAttribute("value"),
    selected: ["true", "selected"].includes(this.getAttribute("selected"))
  }

  this.targetElements.forEach(select => select.slimSelect?.addOption(option))
}
